import { useRef, useState, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config/api.js';

export const useGooglePlacesAutocomplete = (onPlaceSelected) => {
  const inputElementRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimerRef = useRef(null);

  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching address suggestions for:', input);
      const response = await axios.get(`${API_URL}/api/geocoding/autocomplete`, {
        params: { input }
      });

      console.log('Autocomplete response:', response.data);

      if (response.data.predictions) {
        setSuggestions(response.data.predictions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Instead of using addEventListener, return an onChange handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSuggestionClick = async (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const response = await axios.get(`${API_URL}/api/geocoding/details`, {
        params: { placeId: suggestion.place_id }
      });

      const place = response.data.result;
      
      if (place && place.address_components) {
        const addressComponents = {};
        place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            addressComponents.street_number = component.long_name;
          }
          if (types.includes('route')) {
            addressComponents.route = component.long_name;
          }
          if (types.includes('locality')) {
            addressComponents.city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            addressComponents.state = component.short_name;
          }
          if (types.includes('postal_code')) {
            addressComponents.zip = component.long_name;
          }
        });

        const streetAddress = [
          addressComponents.street_number,
          addressComponents.route
        ].filter(Boolean).join(' ');

        onPlaceSelected({
          address: streetAddress || place.formatted_address,
          city: addressComponents.city || '',
          state: addressComponents.state || '',
          zip: addressComponents.zip || '',
        });

        if (inputElementRef.current) {
          inputElementRef.current.value = streetAddress || place.formatted_address;
        }
      }
    } catch (error) {
      console.error('Place details error:', error);
    }
  };

  const setInputRef = useCallback((element) => {
    inputElementRef.current = element;
    if (element) {
      console.log('Address input ref set via callback');
    }
  }, []);

  return { 
    inputRef: setInputRef, 
    suggestions, 
    loading, 
    showSuggestions, 
    handleSuggestionClick,
    handleInputChange // Return this so the component can use it
  };
};